import { useState, useEffect, useCallback, useRef } from "react";
import Landing   from "./pages/Landing";
import Login     from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Admin     from "./pages/Admin";
import PrivacyPolicy  from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import { ThemeProvider } from "./theme";

const PATH_TO_PAGE = {
  "/": "landing", "/login": "login", "/dashboard": "dashboard",
  "/admin": "admin", "/privacy": "privacy", "/terms": "terms",
};
const PAGE_TO_PATH = {
  landing: "/", login: "/login", dashboard: "/dashboard",
  admin: "/admin", privacy: "/privacy", terms: "/terms",
};

function pageForPath(pathname, hasToken) {
  const page = PATH_TO_PAGE[pathname] || "landing";
  if ((page === "dashboard" || page === "admin") && !hasToken) return "login";
  if (page === "login" && hasToken) return "dashboard";
  return page;
}

export default function App() {
  const [token,     setToken]     = useState(localStorage.getItem("zeb_token")      || "");
  const [userId,    setUserId]    = useState(localStorage.getItem("zeb_user_id")    || "");
  const [tier,      setTier]      = useState(localStorage.getItem("zeb_tier")       || "");
  const [createdAt, setCreatedAt] = useState(localStorage.getItem("zeb_created_at") || "");
  const [isAdmin,   setIsAdmin]   = useState(localStorage.getItem("zeb_is_admin") === "true");
  const [page, setPage] = useState(() => pageForPath(window.location.pathname, !!localStorage.getItem("zeb_token")));
  const [expiredLogout, setExpiredLogout] = useState(false);

  // ── Theme — persisted to localStorage ──────────────────────────────────────
  // Only a theme the user actually picked is remembered (rbxwar_theme_v2); everyone else gets the default.
  const [theme, setTheme] = useState(() => localStorage.getItem("rbxwar_theme_v2") || "mono");
  const handleThemeChange = useCallback((t) => {
    setTheme(t);
    localStorage.setItem("rbxwar_theme_v2", t);
  }, []);

  const navigate = useCallback((newPage, { replace = false } = {}) => {
    const path = PAGE_TO_PATH[newPage] || "/";
    if (replace) {
      window.history.replaceState(null, "", path);
    } else if (window.location.pathname !== path) {
      window.history.pushState(null, "", path);
    }
    setPage(newPage);
  }, []);

  useEffect(() => {
    const onPopState = () => {
      setPage(pageForPath(window.location.pathname, !!localStorage.getItem("zeb_token")));
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    const correctPage = pageForPath(window.location.pathname, !!token);
    if (correctPage !== page) navigate(correctPage, { replace: true });
  }, []);

  const handleLogin = ({ token, user_id, tier, created_at, is_admin }) => {
    setToken(token); setUserId(user_id); setTier(tier);
    setCreatedAt(created_at || ""); setIsAdmin(!!is_admin);
    localStorage.setItem("zeb_token",      token);
    localStorage.setItem("zeb_user_id",    user_id);
    localStorage.setItem("zeb_tier",       tier);
    localStorage.setItem("zeb_created_at", created_at || "");
    localStorage.setItem("zeb_is_admin",   is_admin ? "true" : "false");
    navigate("dashboard");
  };

  const handleLogout = (reason) => {
    setToken(""); setUserId(""); setTier(""); setCreatedAt(""); setIsAdmin(false);
    const chosenTheme = localStorage.getItem("rbxwar_theme_v2");
    localStorage.clear();
    // A deliberately chosen theme shouldn't vanish on logout
    if (chosenTheme) localStorage.setItem("rbxwar_theme_v2", chosenTheme);
    if (reason === "EXPIRED") {
      setExpiredLogout(true);
      navigate("login");
    } else {
      setExpiredLogout(false);
      navigate("landing");
    }
  };

  useEffect(() => {
    document.title = page === "dashboard" && token ? "Dashboard" : "RBXWar";
  }, [page, token]);

  const logoutRef = useRef(handleLogout);
  logoutRef.current = handleLogout;
  useEffect(() => {
    const onExpired = () => {
      if (localStorage.getItem("zeb_token")) logoutRef.current();
    };
    window.addEventListener("rbxwar:session-expired", onExpired);
    return () => window.removeEventListener("rbxwar:session-expired", onExpired);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      {page === "privacy" && (
        <PrivacyPolicy onBack={() => navigate(token ? "dashboard" : "landing")} />
      )}
      {page === "terms" && (
        <TermsOfService onBack={() => navigate(token ? "dashboard" : "landing")} />
      )}
      {page === "dashboard" && token && (
        <Dashboard
          token={token} userId={userId} tier={tier} createdAt={createdAt} isAdmin={isAdmin}
          onLogout={handleLogout}
          onAdmin={() => navigate("admin")}
          theme={theme}
          onThemeChange={handleThemeChange}
        />
      )}
      {page === "admin" && token && (
        <Admin onBack={() => navigate("dashboard")} />
      )}
      {page === "login" && !token && (
        <Login onLogin={handleLogin} onBack={() => navigate("landing")} initialExpired={expiredLogout} onExpiredDismiss={() => setExpiredLogout(false)} />
      )}
      {(page === "landing" || (!token && page !== "login" && page !== "privacy" && page !== "terms")) && (
        <Landing
          isLoggedIn={!!token}
          onLogin={() => navigate(token ? "dashboard" : "login")}
          onBuy={() => window.open("https://rbxwar.mysellauth.com/", "_blank")}
          onPrivacy={() => navigate("privacy")}
          onTerms={() => navigate("terms")}
        />
      )}
    </ThemeProvider>
  );
}
